/* import __COLOCATED_TEMPLATE__ from './location-selector.hbs.js'; */
var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _class, _class2, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
/**
### Overview

This "smart" component abstracts away most of the complexity of selecting
locations in Dashboard. This component replaces `sq-subunits-filter`.

This component gets all of the location data directly from the `location-selector`
service. You should configure that service in the model hook of any route that will
render this component, in order to set up single/multiple selection and/or any initial
selection of locations.

### Basic Usage

In your route:
```javascript
{
  locationSelector: service(),

  // Route will block loading until this promise resolves
  model() {
    return this.locationSelector.configure().then(selectedUnits => {
      // fetch some data using tokens from selectedUnits
      // and return model data
    });
  }
}
```

In your controller:
```javascript
{
  actions: {
    selectedUnitChange(units) {
      // fetch some data about units
    },

    trackOpen() {
      // track some data to ES2 when the selector is opened
    }
  }
}
```

In your template:
```handlebars
<LocationSelector
  @selectionChanged={{action "selectedUnitChange"}}
  @onOpen={{action "trackOpen"}} />
```

The component and service manage all selected location state. Your controller
can use the `selectionChanged` action to be notified of changes to the selected
locations (and make any subsequent updates it may need). If the controller needs
to force the selection to change, it should re-call `configure` on the service and
provide the tokens that should be selected.

### Parameters
@param {String} [dropdownClass] Override class that styles dropdown.
@param {Boolean} [isDisabled=false] Set to `true` to disable location dropdown entirely and not allow for any selections to be made.
*/

import { tagName } from '@ember-decorators/component';
import { action } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
let LocationSelector = (_dec = tagName(''), _dec2 = readOnly('locationSelector.isLoading'), _dec3 = readOnly('locationSelector.multiple'), _dec4 = readOnly('locationSelector.sortedUnits'), _dec5 = readOnly('locationSelector.selectedUnits'), _dec6 = readOnly('locationSelector.hideIfOneUnit'), _dec(_class = (_class2 = class LocationSelector extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "locationSelector", _descriptor, this);
    // Optional values that can be overridden
    _defineProperty(this, "dropdownClass", null);
    _defineProperty(this, "alignRight", false);
    _defineProperty(this, "isDisabled", false);
    _defineProperty(this, "isMarketUi", false);
    _defineProperty(this, "hideMarketSelectableCount", false);
    _defineProperty(this, "filterButtonSize", 'medium');
    // Get all state from the service directly. Mark as readOnly to prevent
    // accidental writes to service state.
    _initializerDefineProperty(this, "isLoading", _descriptor2, this);
    _initializerDefineProperty(this, "multiple", _descriptor3, this);
    _initializerDefineProperty(this, "units", _descriptor4, this);
    _initializerDefineProperty(this, "selectedUnits", _descriptor5, this);
    _initializerDefineProperty(this, "hideIfOneUnit", _descriptor6, this);
  }
  trackInactiveSelections(isDefaultSelection = false) {
    this.locationSelector.trackInactiveSelections({
      selectedUnits: this.selectedUnits,
      isMultipleSelection: this.multiple,
      isDefaultSelection,
      isNewSelector: true
    });
  }
  didInsertElement() {
    super.didInsertElement();

    // This should no-op, provided that the route or controller has already
    // loaded the units, but this will prevent the component from ever being
    // rendered without locationSelector data
    this.locationSelector.loadUnits();

    // WFE-1569 Track inactive selections as part of A/B test
    this.trackInactiveSelections(true);
  }
  handleSelectionChanged(units) {
    // Write change to locationSelector service (global state)
    this.locationSelector.set('selectedUnits', units);

    // Bubble up to parent
    this.selectionChanged?.(units);

    // WFE-1569 Track inactive selections as part of A/B test
    this.trackInactiveSelections();
  }
}, _descriptor = _applyDecoratedDescriptor(_class2.prototype, "locationSelector", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class2.prototype, "isLoading", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class2.prototype, "multiple", [_dec3], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class2.prototype, "units", [_dec4], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class2.prototype, "selectedUnits", [_dec5], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor6 = _applyDecoratedDescriptor(_class2.prototype, "hideIfOneUnit", [_dec6], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class2.prototype, "handleSelectionChanged", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "handleSelectionChanged"), _class2.prototype), _class2)) || _class);
export { LocationSelector as default };